import { Breakpoint } from "@omni/types/enums/breakPoint.enum";

export type ColSize = "auto" | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 12;
export type ColOffset = 3;

export const COL_DEFAULT_CLASS =
    "tw:basis-0 tw:grow tw:max-w-full tw:relative tw:w-full tw:px-[1.4%]";

const ColVariants: Record<Breakpoint, Record<ColSize, string>> = {
    xs: {
        auto: "tw:basis-auto tw:w-auto tw:mw-auto",
        1: "tw:basis-1/12 tw:max-w-1/12",
        2: "tw:basis-2/12 tw:max-w-2/12",
        3: "tw:basis-3/12 tw:max-w-3/12",
        4: "tw:basis-4/12 tw:max-w-4/12",
        5: "tw:basis-5/12 tw:max-w-5/12",
        6: "tw:basis-6/12 tw:max-w-6/12",
        7: "tw:basis-7/12 tw:max-w-7/12",
        8: "tw:basis-8/12 tw:max-w-8/12",
        9: "tw:basis-9/12 tw:max-w-9/12",
        10: "tw:basis-10/12 tw:max-w-10/12",
        12: "tw:basis-full tw:max-w-full"
    },
    sm: {
        auto: "tw:sm:basis-auto tw:sm:w-auto tw:sm:mw-auto",
        1: "tw:sm:basis-1/12 tw:sm:max-w-1/12",
        2: "tw:sm:basis-2/12 tw:sm:max-w-2/12",
        3: "tw:sm:basis-3/12 tw:sm:max-w-3/12",
        4: "tw:sm:basis-4/12 tw:sm:max-w-4/12",
        5: "tw:sm:basis-5/12 tw:sm:max-w-5/12",
        6: "tw:sm:basis-6/12 tw:sm:max-w-6/12",
        7: "tw:sm:basis-7/12 tw:sm:max-w-7/12",
        8: "tw:sm:basis-8/12 tw:sm:max-w-8/12",
        9: "tw:sm:basis-9/12 tw:sm:max-w-9/12",
        10: "tw:sm:basis-10/12 tw:sm:max-w-10/12",
        12: "tw:sm:basis-full tw:sm:max-w-full"
    },
    md: {
        auto: "tw:md:basis-auto tw:md:w-auto tw:md:mw-auto",
        1: "tw:md:basis-1/12 tw:md:max-w-1/12",
        2: "tw:md:basis-2/12 tw:md:max-w-2/12",
        3: "tw:md:basis-3/12 tw:md:max-w-3/12",
        4: "tw:md:basis-4/12 tw:md:max-w-4/12",
        5: "tw:md:basis-5/12 tw:md:max-w-5/12",
        6: "tw:md:basis-6/12 tw:md:max-w-6/12",
        7: "tw:md:basis-7/12 tw:md:max-w-7/12",
        8: "tw:md:basis-8/12 tw:md:max-w-8/12",
        9: "tw:md:basis-9/12 tw:md:max-w-9/12",
        10: "tw:md:basis-10/12 tw:md:max-w-10/12",
        12: "tw:md:basis-full tw:md:max-w-full"
    },
    lg: {
        auto: "tw:lg:basis-auto tw:lg:w-auto tw:lg:mw-auto",
        1: "tw:lg:basis-1/12 tw:lg:max-w-1/12",
        2: "tw:lg:basis-2/12 tw:lg:max-w-2/12",
        3: "tw:lg:basis-3/12 tw:lg:max-w-3/12",
        4: "tw:lg:basis-4/12 tw:lg:max-w-4/12",
        5: "tw:lg:basis-5/12 tw:lg:max-w-5/12",
        6: "tw:lg:basis-6/12 tw:lg:max-w-6/12",
        7: "tw:lg:basis-7/12 tw:lg:max-w-7/12",
        8: "tw:lg:basis-8/12 tw:lg:max-w-8/12",
        9: "tw:lg:basis-9/12 tw:lg:max-w-9/12",
        10: "tw:lg:basis-10/12 tw:lg:max-w-10/12",
        12: "tw:lg:basis-full tw:lg:max-w-full"
    },
    xl: {
        auto: "tw:xl:basis-auto tw:xl:w-auto tw:xl:mw-auto",
        1: "tw:xl:basis-1/12 tw:xl:max-w-1/12",
        2: "tw:xl:basis-2/12 tw:xl:max-w-2/12",
        3: "tw:xl:basis-3/12 tw:xl:max-w-3/12",
        4: "tw:xl:basis-4/12 tw:xl:max-w-4/12",
        5: "tw:xl:basis-5/12 tw:xl:max-w-5/12",
        6: "tw:xl:basis-6/12 tw:xl:max-w-6/12",
        7: "tw:xl:basis-7/12 tw:xl:max-w-7/12",
        8: "tw:xl:basis-8/12 tw:xl:max-w-8/12",
        9: "tw:xl:basis-9/12 tw:xl:max-w-9/12",
        10: "tw:xl:basis-10/12 tw:xl:max-w-10/12",
        12: "tw:xl:basis-full tw:xl:max-w-full"
    }
};

const ColOffsetVariants: Record<Breakpoint, Record<ColOffset, string>> = {
    xs: {
        3: "tw:ml-[calc(3/12*100%)]"
    },
    sm: {
        3: "tw:sm:ml-[calc(3/12*100%)]"
    },
    md: {
        3: "tw:md:ml-[calc(3/12*100%)]"
    },
    lg: {
        3: "tw:lg:ml-[calc(3/12*100%)]"
    },
    xl: {
        3: "tw:xl:ml-[calc(3/12*100%)]"
    }
};

export const generateColClasses = (
    breakPoint: Breakpoint = Breakpoint.xs,
    colSize?: ColSize
) => {
    if (!colSize) {
        return "";
    }

    const variantClasses = ColVariants[breakPoint] || ColVariants.xs;
    return ` ${variantClasses[colSize] || ""}`;
};

export const generateOffsetClasses = (
    breakPoint: Breakpoint = Breakpoint.xs,
    offset?: ColOffset
) => {
    if (!offset) {
        return "";
    }

    const breakPointOffsetClasses =
        ColOffsetVariants[breakPoint] || ColOffsetVariants.xs;
    return breakPointOffsetClasses[offset] || "";
};
